import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Divider,
    FormControlLabel,
    Switch,
    TextField,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import CommentIcon from '@material-ui/icons/Comment'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../../../helpers'
import {
    AuditAttribute,
    AuditResidentTerm,
    AuditValueStatus,
    TaskProgressResponse,
} from '../../types'
import { statusFromValuesMatch } from './auditValueStatus'
import { useAuditAttributeDetailStyles } from './styles'
import { AuditResultsContent } from './AuditResultsContent'

interface AuditAttributeDetailProps {
    auditId: number
    open: boolean
    onClose: () => void
    attribute: AuditAttribute
    mismatchResidentTerms: AuditResidentTerm[]
    rentRollFile?: string
    isLoading?: boolean
    error?: Error | null
    page: number
    totalPages: number
    totalCount: number
    onUpdateDocumentValue?: (
        residentTermId: number,
        attributeId: number,
        documentValue: string | null,
    ) => Promise<void> | void
    isUpdatingDocumentValue?: boolean
    onSaveReviewNote?: (
        residentTermId: number,
        attributeId: number,
        note: string | null,
    ) => Promise<void> | void
    isSavingReviewNote?: boolean
}

interface AttributeDetailRow {
    termId: number
    resident: string | null
    leaseStart: string | null
    leaseEnd: string | null
    bldgUnit: string | null
    rentRollValue: string | null
    documentValue: string | null
    status: AuditValueStatus | null
    hasReviewNote: boolean
}

const getStatusLabel = (status: AuditValueStatus | null): string => {
    if (!status) return '-'

    switch (status) {
        case 'MATCHED':
            return 'Matched'
        case 'NOT_MATCHED':
            return 'Not Matched'
        case 'MISSING_RENT_ROLL':
            return 'Missing Rent Roll'
        case 'MISSING_DOCUMENT':
            return 'Missing Document'
    }
}

const formatLeaseRange = (start: string | null, end: string | null): string => {
    if (start && end) {
        return `${start} → ${end}`
    }

    return start ?? end ?? '-'
}

interface AttributePromptDetail {
    id: number
    prompt: string
    example_image: string | null
}

const formatTermSummary = (
    resident: string | null,
    bldgUnit: string | null,
): string => {
    const parts = [
        resident,
        bldgUnit ? `Unit ${bldgUnit}` : null,
    ].filter((part): part is string => Boolean(part))

    return parts.length > 0 ? parts.join(' • ') : 'Resident'
}

export const AuditAttributeDetail: React.FC<AuditAttributeDetailProps> = ({
    auditId,
    open,
    onClose,
    attribute,
    mismatchResidentTerms,
    rentRollFile,
    isLoading = false,
    error,
    page,
    totalPages,
    totalCount,
    onUpdateDocumentValue,
    isUpdatingDocumentValue = false,
    onSaveReviewNote,
    isSavingReviewNote = false,
}) => {
    const classes = useAuditAttributeDetailStyles()
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [showViewers, setShowViewers] = useState(true)

    const rows = useMemo<AttributeDetailRow[]>(() => {
        return mismatchResidentTerms.map((term) => {
            const attributeValue = term.attribute_values.find(
                (value) => value.attribute_id === attribute.id,
            )

            return {
                termId: term.id,
                resident: term.resident ?? null,
                leaseStart: term.lease_start ?? null,
                leaseEnd: term.lease_end ?? null,
                bldgUnit: term.bldg_unit ?? null,
                rentRollValue: attributeValue?.rent_roll_value ?? null,
                documentValue: attributeValue?.document_value ?? null,
                status: attributeValue
                    ? statusFromValuesMatch(
                          attributeValue.rent_roll_value,
                          attributeValue.document_value,
                          attributeValue.values_match,
                      )
                    : null,
                hasReviewNote: Boolean(attributeValue?.review_note),
            }
        })
    }, [mismatchResidentTerms, attribute.id])

    useEffect(() => {
        setSelectedIndex(0)
    }, [attribute.id, page])

    useEffect(() => {
        if (selectedIndex >= mismatchResidentTerms.length) {
            setSelectedIndex(0)
        }
    }, [mismatchResidentTerms.length, selectedIndex])

    const selectedTerm = mismatchResidentTerms[selectedIndex]
    const selectedValue = selectedTerm?.attribute_values.find(
        (value) => value.attribute_id === attribute.id,
    )
    const selectedStatus = selectedValue
        ? statusFromValuesMatch(
              selectedValue.rent_roll_value,
              selectedValue.document_value,
              selectedValue.values_match,
          )
        : 'NOT_MATCHED'
    const selectedSummary = formatTermSummary(
        selectedTerm?.resident ?? null,
        selectedTerm?.bldg_unit ?? null,
    )
    const selectedRange = formatLeaseRange(
        selectedTerm?.lease_start ?? null,
        selectedTerm?.lease_end ?? null,
    )

    const canMovePrev = selectedIndex > 0
    const canMoveNext = selectedIndex < mismatchResidentTerms.length - 1
    const totalIndex = selectedIndex + 1

    const handlePrev = () => {
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
    }

    const handleNext = () => {
        setSelectedIndex((prev) =>
            Math.min(prev + 1, mismatchResidentTerms.length - 1),
        )
    }

    const handleUpdateDocumentValue = async (nextValue: string | null) => {
        if (!selectedTerm || !onUpdateDocumentValue) return

        await onUpdateDocumentValue(selectedTerm.id, attribute.id, nextValue)
    }

    const handleSaveReviewNote = async (note: string | null) => {
        if (!selectedTerm || !onSaveReviewNote) return

        await onSaveReviewNote(selectedTerm.id, attribute.id, note)
    }

    const queryClient = useQueryClient()
    const exampleInputRef = useRef<HTMLInputElement | null>(null)
    const [promptDraft, setPromptDraft] = useState('')
    const [isEditingPrompt, setIsEditingPrompt] = useState(false)
    const [exampleFile, setExampleFile] = useState<File | null>(null)
    const [examplePreviewUrl, setExamplePreviewUrl] = useState<string | null>(
        null,
    )
    const [removeExample, setRemoveExample] = useState(false)
    const [reextractTaskId, setReextractTaskId] = useState<string | null>(null)
    const [reextractError, setReextractError] = useState<string | null>(null)
    const [isStartingReextract, setIsStartingReextract] = useState(false)

    const { data: attributeDetail } = useQuery<
        AxiosResponse<AttributePromptDetail>,
        Error
    >({
        queryKey: ['leaseAuditAttribute', auditId, attribute.id],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/attributes/${attribute.id}/`,
            ),
        enabled: open,
    })

    const promptLoaded = attributeDetail?.data?.id === attribute.id

    useEffect(() => {
        if (!promptLoaded) return
        setPromptDraft(attributeDetail?.data?.prompt || '')
    }, [attribute.id, promptLoaded])

    useEffect(() => {
        setReextractTaskId(null)
        setReextractError(null)
        setIsEditingPrompt(false)
        setExampleFile(null)
        setRemoveExample(false)
        setExamplePreviewUrl((current) => {
            if (current) URL.revokeObjectURL(current)
            return null
        })
    }, [attribute.id])

    const { data: reextractStatus } = useQuery<
        AxiosResponse<TaskProgressResponse>,
        Error
    >({
        queryKey: ['attributeReextract', auditId, reextractTaskId],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/documents/extracting/task-status/${reextractTaskId}/`,
            ),
        enabled: Boolean(reextractTaskId),
        refetchInterval: (query) => {
            if (!query) return false
            const latestData = (query as { state?: { data?: AxiosResponse<TaskProgressResponse> } })
                .state?.data
            const status = latestData?.data?.task_status
            if (status === 'SUCCESS' || status === 'FAILED') return false
            return 2000
        },
    })

    const taskStatus = reextractStatus?.data?.task_status
    const extractionSubTask = reextractStatus?.data?.sub_tasks?.find(
        (subTask) => subTask.sub_task_type === 'DATA_EXTRACTION',
    )
    const isReextracting =
        isStartingReextract ||
        (Boolean(reextractTaskId) &&
            taskStatus !== 'SUCCESS' &&
            taskStatus !== 'FAILED')

    useEffect(() => {
        if (!reextractTaskId || !taskStatus) return
        if (taskStatus !== 'SUCCESS' && taskStatus !== 'FAILED') return

        queryClient.invalidateQueries({
            queryKey: ['leaseAuditDetail', auditId],
        })

        if (taskStatus === 'SUCCESS') {
            queryClient.invalidateQueries({
                queryKey: ['attributeMismatches', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['auditResults', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['attributeSummary', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRollAttributes', auditId],
            })
            setReextractTaskId(null)
            setIsEditingPrompt(false)
            setExampleFile(null)
            setRemoveExample(false)
            setExamplePreviewUrl((current) => {
                if (current) URL.revokeObjectURL(current)
                return null
            })
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditAttribute', auditId, attribute.id],
            })
            return
        }

        setReextractError(
            extractionSubTask?.results.sub_task_error ||
                reextractStatus?.data?.task_error ||
                'Re-extraction failed.',
        )
    }, [
        taskStatus,
        reextractTaskId,
        auditId,
        queryClient,
        extractionSubTask?.results.sub_task_error,
        reextractStatus?.data?.task_error,
        attribute.id,
    ])

    const progressCurrent = extractionSubTask?.results.currently_on_record
    const progressTotal = extractionSubTask?.results.total_records
    const progressLabel = !isReextracting
        ? null
        : progressTotal
          ? `Re-extracting this attribute, document ${progressCurrent ?? 0} of ${progressTotal}.`
          : 'Re-extracting this attribute.'

    const savedPrompt = attributeDetail?.data?.prompt || ''
    const savedExampleUrl = attributeDetail?.data?.example_image || null
    const shownExampleUrl =
        examplePreviewUrl || (removeExample ? null : savedExampleUrl)

    const handleEditPrompt = () => {
        setPromptDraft(savedPrompt)
        setExampleFile(null)
        setRemoveExample(false)
        setExamplePreviewUrl((current) => {
            if (current) URL.revokeObjectURL(current)
            return null
        })
        setIsEditingPrompt(true)
    }

    const handleCancelPromptEdit = () => {
        setPromptDraft(savedPrompt)
        setExampleFile(null)
        setRemoveExample(false)
        setExamplePreviewUrl((current) => {
            if (current) URL.revokeObjectURL(current)
            return null
        })
        setIsEditingPrompt(false)
    }

    const handleExampleSelected = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        setExampleFile(file)
        setRemoveExample(false)
        setExamplePreviewUrl((current) => {
            if (current) URL.revokeObjectURL(current)
            return URL.createObjectURL(file)
        })
    }

    const handleRemoveExample = () => {
        setExampleFile(null)
        setRemoveExample(true)
        setExamplePreviewUrl((current) => {
            if (current) URL.revokeObjectURL(current)
            return null
        })
    }

    const handleReextract = async () => {
        setReextractError(null)
        setIsStartingReextract(true)
        try {
            const attributeUrl = `/lease-audit/${auditId}/attributes/${attribute.id}/`
            if (exampleFile) {
                const form = new FormData()
                form.append('prompt', promptDraft)
                form.append('example_image', exampleFile)
                await axiosInstance.patch(attributeUrl, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
            } else if (removeExample) {
                await axiosInstance.patch(attributeUrl, {
                    prompt: promptDraft,
                    example_image: null,
                })
            } else {
                await axiosInstance.patch(attributeUrl, { prompt: promptDraft })
            }
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditAttribute', auditId, attribute.id],
            })
            const response = await axiosInstance.post<{ task_id: string }>(
                `/lease-audit/${auditId}/documents/reextract-attribute/`,
                { attribute_id: attribute.id },
            )
            setReextractTaskId(response.data.task_id)
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRollAttributes', auditId],
            })
        } catch (requestError) {
            const responseData = (
                requestError as {
                    response?: {
                        data?: {
                            message?: string
                            detail?: string
                            example_image?: string | string[]
                        }
                    }
                }
            ).response?.data
            const imageError = responseData?.example_image
            const responseMessage =
                responseData?.message ||
                responseData?.detail ||
                (Array.isArray(imageError) ? imageError[0] : imageError)
            setReextractError(
                responseMessage || 'Unable to start re-extraction.',
            )
        } finally {
            setIsStartingReextract(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                className: classes.dialogPaper,
                style: {
                    height: '90vh',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Typography variant="h6">
                    {attribute.name} Mismatches
                </Typography>
                <Button
                    variant="contained"
                    className={classes.darkButton}
                    onClick={handleEditPrompt}
                    disabled={
                        !promptLoaded || isReextracting || isEditingPrompt
                    }
                >
                    Edit Prompt
                </Button>
            </DialogTitle>
            <DialogContent
                dividers
                className={classes.dialogContent}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                }}
            >
                {isEditingPrompt && (
                    <Box className={classes.promptBar}>
                            <TextField
                                label="Prompt"
                                value={promptDraft}
                                onChange={(event) =>
                                    setPromptDraft(event.target.value)
                                }
                                multiline
                                rows={2}
                                fullWidth
                                variant="outlined"
                                disabled={!promptLoaded || isReextracting}
                            />
                            {shownExampleUrl && (
                                <img
                                    src={shownExampleUrl}
                                    alt="Example with the value marked"
                                    className={classes.examplePreview}
                                />
                            )}
                            <input
                                ref={exampleInputRef}
                                className={classes.hiddenFileInput}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                onChange={handleExampleSelected}
                            />
                            <Box className={classes.promptActions}>
                                <Button
                                    variant="contained"
                                    className={classes.darkButton}
                                    onClick={() =>
                                        exampleInputRef.current?.click()
                                    }
                                    disabled={isReextracting}
                                >
                                    Upload Example
                                </Button>
                                {(shownExampleUrl || savedExampleUrl) && (
                                    <Button
                                        variant="contained"
                                        className={classes.darkButton}
                                        onClick={handleRemoveExample}
                                        disabled={isReextracting}
                                    >
                                        Remove Example
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    className={classes.darkButton}
                                    onClick={handleReextract}
                                    disabled={!promptLoaded || isReextracting}
                                >
                                    {isReextracting
                                        ? 'Re-extracting...'
                                        : 'Re-extract Attribute'}
                                </Button>
                                <Button
                                    onClick={handleCancelPromptEdit}
                                    disabled={isReextracting}
                                >
                                    Cancel
                                </Button>
                                {progressLabel && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {progressLabel}
                                    </Typography>
                                )}
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Upload a screenshot with the value highlighted.
                                The highlight is only on the example. Saves the
                                prompt, then re-extracts this attribute. Other
                                attributes and manual edits stay as they are.
                            </Typography>
                            {reextractError && (
                                <Typography variant="body2" color="error">
                                    {reextractError}
                                </Typography>
                            )}
                    </Box>
                )}
                {progressLabel && !isEditingPrompt && (
                    <Typography variant="body2" color="textSecondary">
                        {progressLabel}
                    </Typography>
                )}
                {isLoading && (
                    <Box className={classes.loadingState}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {!isLoading && error && (
                    <Box className={classes.emptyState}>
                        <Typography variant="body2" color="textSecondary">
                            {error.message || 'Unable to load mismatches.'}
                        </Typography>
                    </Box>
                )}
                {!isLoading && !error && mismatchResidentTerms.length === 0 && (
                    <Box className={classes.emptyState}>
                        <Typography variant="body2" color="textSecondary">
                            No mismatches found for this attribute.
                        </Typography>
                    </Box>
                )}
                {!isLoading && !error && mismatchResidentTerms.length > 0 && (
                    <Box className={classes.contentGrid}>
                        <Box className={classes.sidebar}>
                            <Box className={classes.sidebarHeader}>
                                <Box className={classes.sidebarHeaderControls}>
                                    <Button
                                        onClick={handlePrev}
                                        disabled={!canMovePrev}
                                    >
                                        Prev
                                    </Button>
                                    <Button
                                        onClick={handleNext}
                                        disabled={!canMoveNext}
                                    >
                                        Next
                                    </Button>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {totalIndex} of {totalCount}
                                    </Typography>
                                </Box>
                            </Box>
                            <List className={classes.termList}>
                                {rows.map((row, index) => (
                                    <ListItem
                                        key={row.termId}
                                        button
                                        onClick={() => setSelectedIndex(index)}
                                        className={`${classes.termListItem} ${
                                            index === selectedIndex
                                                ? classes.termListItemActive
                                                : ''
                                        }`}
                                    >
                                        <span
                                            className={`${classes.statusDot} ${
                                                row.status === 'MATCHED'
                                                    ? classes.statusDotMatched
                                                    : row.status ===
                                                      'NOT_MATCHED'
                                                    ? classes.statusDotNotMatched
                                                    : classes.statusDotMissing
                                            }`}
                                        />
                                        <ListItemText
                                            primary={formatTermSummary(
                                                row.resident,
                                                row.bldgUnit,
                                            )}
                                            secondary={`${formatLeaseRange(
                                                row.leaseStart,
                                                row.leaseEnd,
                                            )}  `}
                                        />
                                        {row.hasReviewNote && (
                                            <CommentIcon
                                                fontSize="small"
                                                color="action"
                                            />
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                            {totalPages > 1 && (
                                <Box className={classes.pagination}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box className={classes.detailPanel}>
                            {selectedTerm && selectedValue ? (
                                <AuditResultsContent
                                    attributeName={attribute.name}
                                    title={`${attribute.name} • ${selectedSummary}`}
                                    subtitle={selectedRange}
                                    rentRollFile={rentRollFile}
                                    rentRollCoordinates={
                                        selectedValue.rent_roll_coordinates
                                    }
                                    leaseDocumentFile={
                                        selectedValue.document_file ||
                                        selectedTerm.selected_document_file ||
                                        ''
                                    }
                                    documentPageNumber={
                                        selectedValue.document_page_number
                                    }
                                    rentRollValue={
                                        selectedValue.rent_roll_value
                                    }
                                    documentValue={selectedValue.document_value}
                                    conflictValue={selectedValue.conflict_value}
                                    status={selectedStatus}
                                    reviewNote={selectedValue.review_note}
                                    reviewNoteAuthor={
                                        selectedValue.review_note_author_name
                                    }
                                    onSaveReviewNote={handleSaveReviewNote}
                                    isSavingReviewNote={isSavingReviewNote}
                                    onUpdateDocumentValue={
                                        handleUpdateDocumentValue
                                    }
                                    isUpdatingDocumentValue={
                                        isUpdatingDocumentValue
                                    }
                                    headerLayout="inline"
                                    showViewers={showViewers}
                                />
                            ) : (
                                <Box className={classes.emptyState}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        No attribute details available for this
                                        term.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    variant="contained"
                    className={classes.darkButton}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
